import React, { useState, useMemo } from 'react';
import { Text, Box, Heading, Select, VStack, IconButton, Collapse } from '@chakra-ui/react';
import useFileUploadsFromActiveLicenses from '../../../hooks/useFileUploadsFromActiveLicenses';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useContext } from 'react';
import CloudinaryUploadWidget from '../../../../app/components/CloudinaryUploadWidget';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './FileBlock';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

interface FileUpload {
    id: string;
    student_name: string;
    student_email: string;
    student_username: string;
    filename: string;
    file_url: string;
    program_name: string | null;
    program_id: string | null;
    school_name: string | null;
    actual_student_school_name: string | null;
    school_id: string | null;
    slug: string;
    class_id: string | null;
}

interface AdminFileUploadsListProps {
    slug: string;
}

const AdminFileUploadsList: React.FC<AdminFileUploadsListProps> = ({ slug }) => {
    const user = useContext(AuthContext);
    const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
    const { fileUploadsFromActiveLicenses } = useFileUploadsFromActiveLicenses(slug);
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    if (!user?.roles.includes(Role.staff)) {
        return null;
    }

    // Get sorted unique schools from file uploads
    const schools = useMemo(() => {
        const schoolMap = new Map<string, { id: string; name: string }>();
        fileUploadsFromActiveLicenses.forEach((upload: FileUpload) => {
            if (upload.school_id && upload.school_name) {
                schoolMap.set(upload.school_id, {
                    id: upload.school_id,
                    name: upload.school_name,
                });
            }
        });
        return Array.from(schoolMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [fileUploadsFromActiveLicenses]);

    // school filter
    const filteredUploads = useMemo(() => {
        if (!selectedSchoolId) return fileUploadsFromActiveLicenses;
        return fileUploadsFromActiveLicenses.filter(
            (upload: FileUpload) => {

                if (selectedSchoolId === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
                    return upload.class_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID;
                }
                return upload.school_id === selectedSchoolId
            }
        );
    }, [fileUploadsFromActiveLicenses, selectedSchoolId]);

    return (
        <Box mt={8} p={4} borderWidth="1px" borderRadius="lg">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Heading size="md">Admin View: File Uploads from all students</Heading>
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setIsOpen(!isOpen)}
                    variant="ghost"
                />
            </Box>

            <Collapse in={isOpen}>
                <VStack spacing={4} align="stretch">
                    <Box>
                        <Text mb={2}>Filter by School:</Text>
                        <Select
                            placeholder="All Schools"
                            value={selectedSchoolId}
                            onChange={(e) => setSelectedSchoolId(e.target.value)}
                        >
                            {schools
                                .concat({ id: CHALLENGE_VIRTUAL_STUDY_GROUP_ID, name: 'Challenge - virtual school' })
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((school) => (
                                    <option key={school.id} value={school.id}>
                                        {school.name}
                                    </option>
                                ))}
                        </Select>
                    </Box>

                    <Box
                        maxH="700px"
                        overflowY="auto"
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                width: '6px',
                                background: 'gray.100',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'gray.400',
                                borderRadius: '24px',
                            },
                        }}
                    >
                        {filteredUploads.length === 0 ? (
                            <Text>No file uploads found{selectedSchoolId ? ' for selected school' : ''}</Text>
                        ) : (
                            filteredUploads.map((file: FileUpload, index: number) => (
                                <Box
                                    key={`${file.id}-${index}`}
                                    p={4}
                                    mb={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    bg="gray.50"
                                >
                                    <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', margin: '4px', justifyContent: isMobile ? 'flex-start' : 'space-between' }}>
                                        <span style={isMobile ? {} : { width: '260px', maxWidth: '260px' }}>
                                            <Text fontWeight="medium">{file.student_name}</Text>
                                            {!file.student_name && (
                                                <Text fontSize="sm" fontWeight="medium">username: {file.student_username}</Text>
                                            )}
                                            {file.program_name && (
                                                <Text fontSize="sm">Program: {file.program_name}</Text>
                                            )}
                                            {file.actual_student_school_name && (
                                                <Text fontSize="sm">School: {file.actual_student_school_name}</Text>
                                            )}
                                            <Text fontSize="sm">slug: {file.slug}</Text>
                                        </span>
                                        <CloudinaryUploadWidget
                                            isViewOnly={true}
                                            isImageOnly={false}
                                            initialUrl={file.file_url}
                                            initialFilename={file.filename}
                                            onImageUpload={() => { }}
                                        />
                                    </div>
                                </Box>
                            ))
                        )}
                    </Box>
                </VStack>
            </Collapse>
        </Box>
    );
};

export default AdminFileUploadsList; 